import { Box, Button, Typography, Grid, MenuItem, Select, Divider, InputLabel, FormControl, Alert } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { createSearchParams, useNavigate } from "react-router-dom";
import { getLiveningWorkOrdersbyICP } from "../../../data/mahiApiConnections";
import { GrayTextField } from "../../../utils/GrayTextField";
import { hikoAlert } from "../../../utils/HikoAlert";
import { getInspectionEnabledWorkOrderStatues, plural } from "../../../utils/utilityFunctions";
import AlertLoading from "../../navigation/AlertLoading";
import { MahiContext } from "../../state/MahiContext";
import { hikoGeneratePath } from "@northpowerdev/np-react-utils";

export default function IcpInspection() {

    const [pageLoading, setPageLoading] = useState(false)
    const [icpNumber, setIcpNumber] = useState('')
    const [icpNumberError, setIcpNumberError] = useState(false)
    const [icpNumberHelperText, setIcpNumberHelperText] = useState(' ')
    const [workOrders, setWorkOrders] = useState([]) // E.g. [{workOrderId: "0WO6D000000MO6xWAG", workOrderNumber: "00017684", workType: "Builders Temporary Supply"}, ...]
    const [selectedWorkOrderId, setSelectedWorkOrderId] = useState('')

    const mahiContext = useContext(MahiContext)
    const navigate = useNavigate()

    const handleIcpNumberChange = (event) => {
        const newIcpNumber = event.target.value
        setIcpNumber(newIcpNumber)

        if (newIcpNumber.length !== 6 && newIcpNumber.length !== 15) {
            setIcpNumberError(true)
            setIcpNumberHelperText('Exactly 6 digits or exactly 15 digits and letters, e.g. 571486 or 0000571486NRE5C')
        } else if (!newIcpNumber.match(/^[0-9A-Z]+$/)) {
            setIcpNumberError(true)
            setIcpNumberHelperText('Digits 0-9 or uppercase letters A-Z please')
        } else {
            setIcpNumberError(false)
            setIcpNumberHelperText(' ')
        }
    }

    const handleGoClick = async (e) => {
        e.preventDefault()
        const abortController = new AbortController()
        await setWorkOrdersFromIcp(icpNumber, abortController.signal)
    }

    const setWorkOrdersFromIcp = async (someIcpNumber, abortSignal) => {
        try {
            setPageLoading(true)
            setWorkOrders([])
            const inspectionWorkOrders = await getLiveningWorkOrdersbyICP(mahiContext, abortSignal, someIcpNumber)
            if (Array.isArray(inspectionWorkOrders)) {
                setWorkOrders(inspectionWorkOrders)
            }
        } catch {
            hikoAlert('Work order details not found', 'There was an error fetching work order details for that ICP')
        } finally {
            setPageLoading(false)
        }
    }

    const handleNextClick = () => {
        const urlString = '/connect/icp_inspection_by_work_order'
        const params = {icp_number: icpNumber, work_order_id: selectedWorkOrderId}
        // const path = generatePath(":urlString?:queryString", { urlString, queryString: createSearchParams(params).toString() });
        const path = hikoGeneratePath(urlString, createSearchParams(params).toString())
        navigate(path)
    }

    const handleSelectWorkOrder = (event) => {
        setSelectedWorkOrderId(event.target.value)
    }

    useEffect(() => {

        const abortController = new AbortController()

        const searchParams = new URLSearchParams(window.location.search)
        const paramIcpNumber = searchParams.get('icp_number')
        if (paramIcpNumber) {
            setIcpNumber(paramIcpNumber)
            setWorkOrdersFromIcp(paramIcpNumber, abortController.signal)
        }
    // eslint-disable-next-line
    }, []) // should depend on setWorkOrdersFromIcp but that creates infinite loop

    const gotWorkOrders = (workOrders.length > 0)
    const inspectionStatuses = getInspectionEnabledWorkOrderStatues()
    const disabledWorkOrders = workOrders.filter((wo) => !inspectionStatuses.includes(wo.status))

    // const counter = useRef(0);
    // console.log('Liven Render COUNT, workOrders', counter.current++, workOrders)

    return (
        <Box className="Page">
            <h2 className="np-title">ICP Inspection - Find ICP</h2>

            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Typography variant="body1" sx={{mb: 2}}>Please provide the Installation Control Point number of the site you are inspecting.</Typography>

                    <form onSubmit={handleGoClick}>
                        <Box sx={{mb: 2}}>
                            <GrayTextField
                                label="ICP Number"
                                value={icpNumber}
                                error={icpNumberError}
                                helperText={icpNumberHelperText}
                                onChange={handleIcpNumberChange} />
                        </Box>
                        <Button type="submit" variant="contained" onClick={handleGoClick} disabled={!icpNumber || icpNumberError}>Go</Button>
                        { pageLoading && <AlertLoading sx={{mt: 2}} spinner={pageLoading} /> }
                        {/* <Button variant="contained" onClick={handleGo401Click} disabled={!icpNumber || icpNumberError}>Go 401</Button> */}
                    </form>
                </Grid>
                {
                    gotWorkOrders
                    &&
                    <Grid item xs={12}>
                        <Divider sx={{my:2}} />
                        <Typography variant="body1" sx={{mb: 1}}>We found {workOrders.length} inspection{plural(workOrders.length)} at ICP {icpNumber}. Please choose one.</Typography>
                        <Box sx={{mb: 2}}>
                            <FormControl>
                                <InputLabel>Work order</InputLabel>
                                <Select value={selectedWorkOrderId} onChange={handleSelectWorkOrder} sx={{minWidth: "12em"}}>
                                {
                                    workOrders.map((thisWorkOrder) => {
                                        const disabled = disabledWorkOrders.includes(thisWorkOrder)
                                        return <MenuItem value={thisWorkOrder.workOrderNumber} key={thisWorkOrder.workOrderNumber} disabled={disabled}>{thisWorkOrder.workOrderNumber} - {thisWorkOrder.workType}</MenuItem>
                                    })
                                }
                                </Select>
                            </FormControl>
                        </Box>

                        <Box  sx={{mb: 2}} textAlign="left">
                            <Typography variant="body2" sx={{mb: 2}}></Typography>
                            {
                                disabledWorkOrders.length > 0
                                &&
                                <Alert sx={{display: 'inline-flex'}} severity="info">We disabled {disabledWorkOrders.length} work order{plural(disabledWorkOrders.length)} in the drop-down menu. To enable ICP inspections, work order status must be one of {inspectionStatuses.join(', ')}.</Alert>
                            }
                            <Alert sx={{display: 'inline-flex', mb: 2}} severity="info">Please call Northpower Customer Care Team on 094301965 if there is any problem with the work orders for this ICP.</Alert>
                        </Box>

                        <Button variant="contained" onClick={handleNextClick} disabled={!selectedWorkOrderId}>Next</Button>
                    </Grid>
                }
            </Grid>

            {/* <DebugBox title="work order">{JSON.stringify(workOrders)}</DebugBox> */}
        </Box>
    )
}







